import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { HowItWorks } from "@/components/HowItWorks";
import { Vantaggi } from "@/components/Vantaggi";
import { Packages } from "@/components/Packages";
import { Gallery } from "@/components/Gallery";
import { About } from "@/components/About";
import { Faq } from "@/components/Faq";
import { AppWaitlist } from "@/components/AppWaitlist";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { AuthModal } from "@/components/AuthModal";
import { BookingModal } from "@/components/BookingModal";
import Privacy from "@/components/Privacy";
import Admin from "@/components/Admin";
import AreaCliente from "@/components/AreaCliente";
import PaymentSuccess from "@/components/PaymentSuccess";
import PaymentCancel from "@/components/PaymentCancel";

function Home() {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [selection, setSelection] = useState(null);
  const [pending, setPending] = useState(null);

  const onBook = useCallback(
    (sel) => {
      if (!sel) {
        document.querySelector("#pacchetti")?.scrollIntoView({ behavior: "smooth" });
        return;
      }
      if (!user) {
        setPending(sel);
        setAuthOpen(true);
      } else {
        setSelection(sel);
      }
    },
    [user]
  );

  const onAuthSuccess = useCallback(() => {
    if (pending) {
      setSelection(pending);
      setPending(null);
    }
  }, [pending]);

  return (
    <div className="bg-[#050507] min-h-screen" data-testid="home-page">
      <Nav onBook={onBook} onAuth={() => setAuthOpen(true)} />
      <main>
        <Hero onBook={onBook} />
        <Marquee />
        <HowItWorks />
        <Vantaggi />
        <Packages onBook={onBook} />
        <Gallery />
        <About />
        <Faq />
        <AppWaitlist />
        <Contact />
      </main>
      <Footer />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onSuccess={onAuthSuccess} />
      <BookingModal selection={selection} onClose={() => setSelection(null)} />
    </div>
  );
}

function App() {
  useEffect(() => {
    if (window.location.pathname !== "/") return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const onClick = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const target = document.querySelector(anchor.getAttribute("href"));
      if (target) {
        e.preventDefault();
        lenis.scrollTo(target, { offset: -80 });
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/area-clienti" element={<AreaCliente />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" theme="dark" richColors />
    </AuthProvider>
  );
}

export default App;
